import { useEffect, useState } from "react";
import { Mail, Copy, Check, RefreshCw, Plus, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function statusStyle(s) {
  const t = (s || "").toLowerCase();
  if (t === "verified") return { color: "#00E5A3", bg: "#00E5A31a", label: "Verified" };
  if (t === "pending") return { color: "#FFB020", bg: "#FFB0201a", label: "Pending — records not seen yet" };
  if (t === "failed") return { color: "#F87171", bg: "#F871711a", label: "Failed" };
  return { color: "#94A3B8", bg: "#94A3B81a", label: t ? t.replace(/_/g, " ") : "Not started" };
}

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text);
    setDone(true);
    setTimeout(() => setDone(false), 1200);
  };
  return (
    <button data-testid="dns-copy-btn" onClick={copy} className="shrink-0 text-slate-500 hover:text-[#00E5A3] transition-colors">
      {done ? <Check size={13} className="text-[#00E5A3]" /> : <Copy size={13} />}
    </button>
  );
}

function RecordsTable({ records }) {
  if (!records?.length) return null;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-left border-collapse min-w-[560px]">
        <thead>
          <tr className="text-[10px] font-mono uppercase text-slate-500">
            <th className="py-1.5 pr-3">Type</th>
            <th className="py-1.5 pr-3">Name / Host</th>
            <th className="py-1.5 pr-3">Value</th>
            <th className="py-1.5 pr-3">Priority</th>
            <th className="py-1.5">Status</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r, i) => {
            const st = statusStyle(r.status);
            return (
              <tr key={i} data-testid={`dns-record-${i}`} className="border-t border-[#1A2133] align-top">
                <td className="py-2 pr-3 font-mono text-[11px] text-[#38BDF8]">{r.type}</td>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-1.5">
                    <code className="text-[11px] text-slate-200 break-all">{r.name || "@"}</code>
                    <CopyBtn text={r.name || "@"} />
                  </div>
                </td>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-1.5 max-w-[300px]">
                    <code className="text-[11px] text-slate-300 break-all">{r.value}</code>
                    <CopyBtn text={r.value} />
                  </div>
                </td>
                <td className="py-2 pr-3 font-mono text-[11px] text-slate-400">{r.priority ?? "—"}</td>
                <td className="py-2">
                  <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ color: st.color, background: st.bg }}>{r.status || "not started"}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function EmailAdmin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domainName, setDomainName] = useState("sidelineiq.io");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/email/domains").then((r) => setData(r.data)).catch(() => setData({ ok: false, error: "Could not load" })).finally(() => setLoading(false));
  useEffect(() => { if (user?.role === "admin") load(); else setLoading(false); }, [user]);

  const addDomain = async () => {
    if (!domainName.trim().includes(".")) { toast.error("Enter a valid domain"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/email/domains", { name: domainName.trim() });
      if (r.data.ok) { toast.success(`Registered ${domainName}. Add the DNS records below.`); load(); }
      else toast.error(r.data.error || "Could not add domain");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add domain"); }
    finally { setBusy(false); }
  };

  const verify = async (id) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/email/domains/verify", { domain_id: id });
      if (r.data.ok) { toast.success("Re-checked DNS records"); load(); }
      else toast.error(r.data.error || "Verify failed");
    } catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
    finally { setBusy(false); }
  };

  if (user && user.role !== "admin") {
    return <div className="max-w-xl mx-auto px-4 py-20 text-center text-slate-400">This area is for administrators only.</div>;
  }

  const domains = data?.domains || [];

  return (
    <div data-testid="email-admin-page" className="max-w-[900px] mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-100 flex items-center gap-2 mb-1">
        <Mail size={22} className="text-[#00E5A3]" /> Email Setup
      </h1>
      <p className="text-sm text-slate-400 mb-5 max-w-2xl">
        Verify your sending domain so alerts, digests and the weekly recap reach real users — not just your own inbox.
        Add the DNS records below at your domain host (GoDaddy, Cloudflare, Namecheap, etc.), then hit <b className="text-slate-300">Re-check</b>.
      </p>

      <div className="flex items-start gap-2 text-[12px] text-slate-400 bg-[#0F1420] border border-[#232D42] rounded-lg px-3 py-2.5 mb-5">
        <AlertCircle size={14} className="text-[#38BDF8] mt-0.5 shrink-0" />
        <span>
          Current sender: <code className="text-slate-200">{data?.sender || "—"}</code>.
          Until your domain shows <b style={{ color: "#00E5A3" }}>Verified</b>, Resend only delivers to your own account email.
          Once verified, ask your developer to set <code className="text-slate-200">SENDER_EMAIL</code> to an address on that domain (e.g. <code className="text-slate-200">alerts@sidelineiq.io</code>).
        </span>
      </div>

      <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-4 mb-6">
        <div className="text-xs font-mono uppercase text-slate-500 mb-2">Add a sending domain</div>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="domain-input" value={domainName} onChange={(e) => setDomainName(e.target.value)}
            placeholder="sidelineiq.io"
            className="flex-1 min-w-[180px] bg-[#0B0F18] border border-[#232D42] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-[#00E5A3] outline-none" />
          <button data-testid="add-domain-btn" onClick={addDomain} disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#00E5A3] text-[#04140F] text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
            <Plus size={14} /> Register with Resend
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-500 text-sm">Loading domains…</div>
      ) : !data?.ok ? (
        <div data-testid="email-error" className="text-center py-10 text-slate-500 text-sm">
          {data?.error === "email not configured" ? "Email isn't configured yet (RESEND_API_KEY missing)." : (data?.error || "Could not load domains.")}
        </div>
      ) : domains.length === 0 ? (
        <div data-testid="no-domains" className="text-center py-10 text-slate-500 text-sm">
          No sending domains yet. Register one above to get your DNS records.
        </div>
      ) : (
        <div className="space-y-4">
          {domains.map((d) => {
            const st = statusStyle(d.status);
            return (
              <div key={d.id} data-testid={`domain-card-${d.name}`} className="bg-[#0F1420] border border-[#232D42] rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold text-slate-100">{d.name}</span>
                    <span data-testid={`domain-status-${d.name}`} className="text-[10px] font-mono uppercase px-2 py-0.5 rounded" style={{ color: st.color, background: st.bg }}>{st.label}</span>
                  </div>
                  <button data-testid={`verify-btn-${d.name}`} onClick={() => verify(d.id)} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#151B2B] text-slate-200 text-xs font-medium hover:bg-[#1C2438] disabled:opacity-50 transition-colors">
                    <RefreshCw size={12} /> Re-check
                  </button>
                </div>
                <RecordsTable records={d.records} />
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-slate-600 mt-6">DNS changes can take minutes to a few hours to propagate. Re-check after adding all records.</p>
    </div>
  );
}
